import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MoreHorizontal, Check, X, Archive, Eye, Send, RotateCcw } from "lucide-react";

const QuickAction = ({ onClick, testId, tooltip, className, children }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild>
      <button onClick={onClick} data-testid={testId}
        className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${className}`}>
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="bg-[#1c1c1c] px-2.5 py-1 text-[11px] text-white">{tooltip}</TooltipContent>
  </Tooltip>
);

// Status-aware quick actions + overflow menu for a table row.
export const RowActions = ({ e, onOpen, onRowAction }) => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-end gap-1">
      {e.status === "needs_review" && (
        <>
          <QuickAction onClick={() => onRowAction(e.id, "approve")} testId={`expenses-row-approve-${e.id}`} tooltip="Approve"
            className="bg-[rgba(34,197,94,0.15)] text-[#4ADE80] hover:bg-[rgba(34,197,94,0.3)]">
            <Check size={13} />
          </QuickAction>
          <QuickAction onClick={() => onRowAction(e.id, "reject")} testId={`expenses-row-reject-${e.id}`} tooltip="Reject"
            className="bg-[rgba(239,68,68,0.12)] text-[#F87171] hover:bg-[rgba(239,68,68,0.26)]">
            <X size={13} />
          </QuickAction>
        </>
      )}
      {e.status === "waiting_on_employee" && e.employee_id && (
        <>
          <QuickAction onClick={() => navigate(`/employees/${e.employee_id}`)} testId={`expenses-row-check-${e.id}`}
            tooltip={`Check — open ${e.employee_name?.split(" ")[0]}'s chat`}
            className="bg-[rgba(79,142,247,0.12)] text-[#7FAEF9] hover:bg-[rgba(79,142,247,0.26)]">
            <Eye size={13} />
          </QuickAction>
          <QuickAction onClick={() => onRowAction(e.id, "message")} testId={`expenses-row-followup-${e.id}`} tooltip="Aria follow up"
            className="bg-[rgba(245,158,11,0.14)] text-[#FBBF24] hover:bg-[rgba(245,158,11,0.28)]">
            <Send size={12} />
          </QuickAction>
        </>
      )}
      {e.status === "rejected" && (
        <QuickAction onClick={() => onRowAction(e.id, "restore")} testId={`expenses-row-restore-${e.id}`} tooltip="Restore to review"
          className="bg-[rgba(163,163,163,0.12)] text-[#A3A3A3] hover:bg-[rgba(163,163,163,0.24)] hover:text-white">
          <RotateCcw size={13} />
        </QuickAction>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`expenses-row-actions-${e.id}`}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#555] opacity-0 transition-opacity hover:bg-[#1f1f1f] hover:text-white group-hover:opacity-100">
            <MoreHorizontal size={14} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-[#222] bg-[#161616] text-white">
          <DropdownMenuItem onClick={() => onOpen(e.id)} className="text-[12px] focus:bg-[#1f1f1f] focus:text-white">View details</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRowAction(e.id, "approve")} className="text-[12px] focus:bg-[#1f1f1f] focus:text-white">Approve</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRowAction(e.id, "reject")} className="text-[12px] text-[#F87171] focus:bg-[#1f1f1f] focus:text-[#F87171]">Reject</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRowAction(e.id, "message")} className="text-[12px] focus:bg-[#1f1f1f] focus:text-white">Ask employee for info</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRowAction(e.id, "export")} className="text-[12px] focus:bg-[#1f1f1f] focus:text-white">Mark export ready</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRowAction(e.id, "archive")} className="text-[12px] text-[#F87171] focus:bg-[#1f1f1f] focus:text-[#F87171]">
            <Archive size={12} className="mr-1.5" /> Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
